/**
 * Single place for “how much is free” vs ledger balance (main unchanged on bet; exposure reserves liability).
 */

export type LedgerBalanceParts = {
  balance?: number
  exposer?: number
  casinoexposer?: number
  bonusBalance?: number
  bonusLocked?: number
  pendingBonusUsed?: number
  availableBalance?: number
  withdrawableBalance?: number
}

/**
 * Amount the user can withdraw right now:
 * balance − total exposure − bonus still under wagering (bonusLocked).
 * Prefer server `withdrawableBalance` when present.
 */
export function computeWithdrawableBalance(b: LedgerBalanceParts): number {
  if (typeof b.withdrawableBalance === 'number' && Number.isFinite(b.withdrawableBalance)) {
    return Math.max(0, Math.round(b.withdrawableBalance * 100) / 100)
  }
  const main = Number(b.balance) || 0
  const locked = Math.max(0, Number(b.bonusLocked) || 0)
  return Math.max(0, Math.round((main - totalExposureFromParts(b) - locked) * 100) / 100)
}

export function totalExposureFromParts(b: LedgerBalanceParts): number {
  return (Number(b.exposer) || 0) + (Number(b.casinoexposer) || 0)
}

/** Main ledger − total exposure. Bonus wallet is separate — not included (admin + client “available”). */
export function computeWithdrawableMainFromLedger(b: LedgerBalanceParts): number {
  const main = Number(b.balance) || 0
  return Math.max(0, Math.round((main - totalExposureFromParts(b)) * 100) / 100)
}

/** Alias for list-clients / ledger: same as main available after exposure (no bonus). */
export function computeAvailableForBetFromLedger(b: LedgerBalanceParts): number {
  return computeWithdrawableMainFromLedger(b)
}

/** Client Redux: `exposer` already includes casino. `bonusBalance` ignored for display — shown only in bonus UI. */
export function computeAvailableForBetCombinedExposer(b: {
  balance: number
  exposer: number
  bonusBalance?: number
}): number {
  return computeWithdrawableMainCombinedExposer({ balance: b.balance, exposer: b.exposer })
}

export function computeWithdrawableMainCombinedExposer(b: {
  balance: number
  exposer: number
  pendingBonusUsed?: number
  availableBalance?: number
}): number {
  return computeRemainingBalance(b)
}

/**
 * Remaining main wallet the user can use (header / withdraw / bet checks):
 * balance − exposure + pendingBonusUsed (stake already paid from bonus wallet).
 * Prefer server `availableBalance` when present.
 */
export function computeRemainingBalance(b: {
  balance?: number
  exposer?: number
  casinoexposer?: number
  pendingBonusUsed?: number
  availableBalance?: number
}): number {
  if (typeof b.availableBalance === 'number' && Number.isFinite(b.availableBalance)) {
    return Math.max(0, Math.round(b.availableBalance * 100) / 100)
  }
  const main = Number(b.balance) || 0
  const exp = totalExposureFromParts(b)
  const fromBonus = Math.max(0, Number(b.pendingBonusUsed) || 0)
  return Math.max(0, Math.round((main - exp + fromBonus) * 100) / 100)
}

type WalletLike = {
  balance: number
  exposer: number
  bonusBalance?: number
  pendingBonusUsed?: number
  availableBalance?: number
}

/**
 * Accounts panel “Credit Limit” slot (B2C): the main wallet ledger balance.
 * `creditRefrences` is informational only and never drives this value.
 */
export function computeCreditLimitDisplay(ledgerBalance: number): number {
  return Math.max(0, Math.round((Number(ledgerBalance) || 0) * 100) / 100)
}

/**
 * Accounts panel “Available Credit” slot (B2C): main ledger − exposure
 * (+ pendingBonusUsed, since that stake was already paid from the bonus wallet).
 * Prefers the server-computed `availableBalance` when present.
 */
export function computeAvailableCreditForAccountPanel(wallet: WalletLike): number {
  return computeRemainingBalance({
    balance: wallet.balance,
    exposer: wallet.exposer,
    pendingBonusUsed: wallet.pendingBonusUsed,
    availableBalance: wallet.availableBalance,
  })
}
